import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Confirm,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useToast } from '../../hooks/useToast';
import BaseModal, { BaseModalAction } from '../../components/shared/BaseModal';
import { apiService } from '../../services/api';
import {
  deviceUserExemptionsService,
  DeviceUserExemptionViewModel,
} from '../../services/authorizedDevices/deviceUserExemptionsService';
import { UserResponse } from '../../types/dtos';

function formatDateTime(value: string | null): string {
  if (!value) return 'Permanente';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
}

function toExpiresAtUtc(dateOnly: string): string | null {
  const trimmed = dateOnly.trim();
  if (!trimmed) return null;
  const parsed = new Date(`${trimmed}T23:59:59`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

const DeviceUserExemptionsPage: React.FC = () => {
  const { showSuccess, showError } = useToast();

  const [exemptions, setExemptions] = useState<DeviceUserExemptionViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createUserId, setCreateUserId] = useState('');
  const [createPermanent, setCreatePermanent] = useState(true);
  const [createValidUntil, setCreateValidUntil] = useState('');
  const [creating, setCreating] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<DeviceUserExemptionViewModel | null>(null);
  const [removing, setRemoving] = useState(false);

  const loadExemptions = useCallback(async () => {
    try {
      setLoading(true);
      const list = await deviceUserExemptionsService.list();
      setExemptions(list);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao carregar liberações');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const page = await apiService.getUsers({ page: 1, pageSize: 500 });
      setUsers(page.items.filter((u) => u.isActive && !u.isDeleted));
    } catch {
      showError('Erro ao carregar usuários para nova liberação');
    } finally {
      setUsersLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void loadExemptions();
  }, [loadExemptions]);

  useEffect(() => {
    if (createOpen) void loadUsers();
  }, [createOpen, loadUsers]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        key: u.id,
        value: u.id,
        text: `${u.nome} (${u.username || u.email})`,
      })),
    [users]
  );

  const handleCreate = async () => {
    if (!createUserId) {
      showError('Selecione o usuário');
      return;
    }
    if (!createPermanent && !createValidUntil.trim()) {
      showError('Informe a data de validade ou marque como permanente');
      return;
    }
    try {
      setCreating(true);
      await deviceUserExemptionsService.grant({
        userId: createUserId,
        expiresAtUtc: createPermanent ? null : toExpiresAtUtc(createValidUntil),
      });
      showSuccess('Liberação criada');
      setCreateOpen(false);
      setCreateUserId('');
      setCreatePermanent(true);
      setCreateValidUntil('');
      await loadExemptions();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao criar liberação');
    } finally {
      setCreating(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      await deviceUserExemptionsService.revoke(removeTarget.userId);
      showSuccess('Liberação removida');
      setRemoveTarget(null);
      await loadExemptions();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao remover liberação');
    } finally {
      setRemoving(false);
    }
  };

  const createActions: BaseModalAction[] = [
    { key: 'cancel', content: 'Cancelar', icon: 'cancel', onClick: () => setCreateOpen(false) },
    {
      key: 'save',
      content: 'Conceder liberação',
      icon: 'check',
      primary: true,
      loading: creating,
      onClick: () => void handleCreate(),
    },
  ];

  return (
    <GlassPageWrapper>
      <Header as="h1" style={{ color: '#fff', marginBottom: '1rem' }}>
        <Icon name="unlock alternate" />
        Liberação por usuário
      </Header>

      <Message info style={{ marginBottom: '1rem' }}>
        Usuários listados aqui podem fazer login sem computador autorizado (modo Enforce). Liberação
        permanente ou com data de validade.
      </Message>

      <Segment inverted style={{ background: 'rgba(255,255,255,0.06)', marginBottom: '1rem' }}>
        <Button primary icon="plus" content="Nova liberação" onClick={() => setCreateOpen(true)} />
      </Segment>

      <Segment inverted style={{ background: 'rgba(255,255,255,0.06)' }}>
        {loading ? (
          <Loader active inline="centered" />
        ) : exemptions.length === 0 ? (
          <Message info>Nenhuma liberação cadastrada.</Message>
        ) : (
          <Table celled striped inverted compact="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Usuário</Table.HeaderCell>
                <Table.HeaderCell>E-mail</Table.HeaderCell>
                <Table.HeaderCell>Validade</Table.HeaderCell>
                <Table.HeaderCell>Atualizado em</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {exemptions.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>{row.displayName}</Table.Cell>
                  <Table.Cell>{row.email || '—'}</Table.Cell>
                  <Table.Cell>
                    {row.isPermanent ? (
                      <Label color="blue" size="small">
                        Permanente
                      </Label>
                    ) : (
                      formatDateTime(row.expiresAtUtc)
                    )}
                  </Table.Cell>
                  <Table.Cell>{formatDateTime(row.createdAt)}</Table.Cell>
                  <Table.Cell textAlign="center">
                    <Button
                      size="mini"
                      color="red"
                      icon="trash"
                      title="Remover liberação"
                      onClick={() => setRemoveTarget(row)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      <BaseModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nova liberação"
        icon="unlock"
        actions={createActions}
      >
        <Form loading={usersLoading}>
          <Form.Dropdown
            fluid
            search
            selection
            label="Usuário"
            placeholder="Selecione o usuário"
            options={userOptions}
            value={createUserId}
            onChange={(_, { value }) => setCreateUserId(String(value ?? ''))}
          />
          <Form.Field>
            <Checkbox
              toggle
              label="Liberação permanente"
              checked={createPermanent}
              onChange={(_, { checked }) => setCreatePermanent(checked ?? true)}
            />
          </Form.Field>
          {!createPermanent && (
            <Form.Input
              type="date"
              label="Válida até"
              value={createValidUntil}
              onChange={(_, { value }) => setCreateValidUntil(String(value))}
            />
          )}
        </Form>
      </BaseModal>

      <Confirm
        open={!!removeTarget}
        header="Remover liberação?"
        content={
          removeTarget
            ? `Remover a liberação de "${removeTarget.displayName}"? O usuário voltará a precisar de computador autorizado no modo Enforce.`
            : ''
        }
        confirmButton="Remover"
        cancelButton="Cancelar"
        loading={removing}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => void handleRemove()}
      />
    </GlassPageWrapper>
  );
};

export default DeviceUserExemptionsPage;
